import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Lock, Users, Ship, ClipboardList, ChevronRight } from "lucide-react";
import { api } from "@/api/client";

function Step({ n, title, body, to, done, locked, icon: Icon }) {
  const tone = done ? "border-emerald-200 bg-emerald-50/60" : locked ? "border-slate-200 bg-slate-50 opacity-60" : "border-cyan-300 bg-white ring-2 ring-cyan-100";
  const inner = (
    <div className={`h-full rounded-xl border p-4 flex gap-3 transition-[border-color,box-shadow] ${tone} ${locked ? "cursor-not-allowed" : "hover:shadow-sm"}`}>
      <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${done ? "bg-emerald-600 text-white" : locked ? "bg-slate-200 text-slate-500" : "bg-[#0b192c] text-white"}`}>
        {done ? <CheckCircle2 className="w-5 h-5" /> : locked ? <Lock className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider text-slate-400">Step {n}</div>
        <div className="font-semibold text-slate-900 flex items-center gap-1">{title}{!locked && <ChevronRight className="w-4 h-4 text-slate-400" />}</div>
        <div className="text-xs text-slate-500 mt-0.5">{body}</div>
        <div className={`text-xs font-medium mt-1.5 ${done ? "text-emerald-700" : locked ? "text-slate-500" : "text-cyan-700"}`} data-testid={`getting-started-step-${n}-state`}>
          {done ? "Completed" : locked ? "Locked — finish the previous step" : "Start here"}
        </div>
      </div>
    </div>
  );
  return locked
    ? <div aria-disabled="true" data-testid={`getting-started-step-${n}`}>{inner}</div>
    : <Link to={to} data-testid={`getting-started-step-${n}`}>{inner}</Link>;
}

export function GettingStartedPanel({ selfId }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/vessels")])
      .then(([u, v]) => setS({ users: u.data.filter((x) => x.id !== selfId).length, vessels: v.data }))
      .catch(() => setS({ users: 0, vessels: [] }));
  }, [selfId]);
  if (!s) return null;
  const hasUsers = s.users > 0, hasVessels = s.vessels.length > 0;
  const hasReqs = s.vessels.some((v) => v.rank_count > 0);
  const target = s.vessels.find((v) => v.rank_count === 0) || s.vessels[0];
  return (
    <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-5" data-testid="getting-started-panel">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="text-sm font-semibold text-slate-800">Getting started</div>
        <div className="text-xs text-slate-500" data-testid="getting-started-progress">{[hasUsers, hasVessels, hasReqs].filter(Boolean).length}/3 complete</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Step n={1} icon={Users} title="Create Users" body="Add your sourcing, docs and management staff." to="/users" done={hasUsers} locked={false} />
        <Step n={2} icon={Ship} title="Create a Vessel" body="Add the vessels you crew." to="/vessels" done={hasVessels} locked={!hasUsers} />
        <Step n={3} icon={ClipboardList} title="Add Crew Requirements" body="Ranks, quantities and sourcing assignments." to={target ? `/vessels/${target.id}` : "/vessels"} done={hasReqs} locked={!hasVessels} />
      </div>
    </div>
  );
}
